import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import FilterBar from '../../components/admin/FilterBar'
import RequestsTable from '../../components/admin/RequestsTable'
import BlockedSlotsManager from '../../components/admin/BlockedSlotsManager'
import AdminUsersManager from '../../components/admin/AdminUsersManager'
import PatientsManager from '../../components/admin/PatientsManager'
import { getNominationRequests, getServiceRequests } from '../../lib/adminQueries'
import './AdminDashboardPage.css'

function AdminDashboardPage() {
  const [activeTab, setActiveTab] = useState('services')
  const [nominationData, setNominationData] = useState([])
  const [serviceData, setServiceData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filters, setFilters] = useState({ searchTerm: '', status: 'all' })
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const [nominationsResult, servicesResult] = await Promise.all([
        getNominationRequests(filters),
        getServiceRequests(filters)
      ])

      if (nominationsResult.error) throw nominationsResult.error
      if (servicesResult.error) throw servicesResult.error

      setNominationData(nominationsResult.data || [])
      setServiceData(servicesResult.data || [])
    } catch (err) {
      console.error('Error fetching data:', err)
      setError(err.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/admin/login')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
  }

  const handleRefresh = () => {
    fetchData()
  }

  const currentData = activeTab === 'nominations' ? nominationData : serviceData
  const currentCount = currentData.length

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="header-content">
          <div>
            <h1>Admin Dashboard</h1>
            <p className="user-email">{user?.email}</p>
          </div>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'nominations' ? 'active' : ''}`}
            onClick={() => setActiveTab('nominations')}
          >
            Nomination Requests
            <span className="tab-count">{nominationData.length}</span>
          </button>
          <button
            className={`tab ${activeTab === 'services' ? 'active' : ''}`}
            onClick={() => setActiveTab('services')}
          >
            Service Requests
            <span className="tab-count">{serviceData.length}</span>
          </button>
          <button
            className={`tab ${activeTab === 'blocked-slots' ? 'active' : ''}`}
            onClick={() => setActiveTab('blocked-slots')}
          >
            Blocked Slots
          </button>
          <button
            className={`tab ${activeTab === 'admin-users' ? 'active' : ''}`}
            onClick={() => setActiveTab('admin-users')}
          >
            Admin Users
          </button>
          <button
            className={`tab ${activeTab === 'patients' ? 'active' : ''}`}
            onClick={() => setActiveTab('patients')}
          >
            Patients
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'patients' ? (
            <PatientsManager />
          ) : activeTab === 'admin-users' ? (
            <AdminUsersManager />
          ) : activeTab === 'blocked-slots' ? (
            <BlockedSlotsManager />
          ) : (
            <>
              <FilterBar
                onFilterChange={handleFilterChange}
                onRefresh={handleRefresh}
                type={activeTab === 'nominations' ? 'nomination' : 'service'}
              />

              {loading && (
                <div className="loading-state">
                  <p>Loading requests...</p>
                </div>
              )}

              {error && (
                <div className="error-state">
                  <p>Error: {error}</p>
                  <button onClick={fetchData} className="retry-button">
                    Retry
                  </button>
                </div>
              )}

              {!loading && !error && (
                <>
                  <div className="results-summary">
                    <p>
                      Showing {currentCount} {activeTab === 'nominations' ? 'nomination' : 'service'} request{currentCount !== 1 ? 's' : ''}
                    </p>
                  </div>

                  <RequestsTable
                    data={currentData}
                    type={activeTab === 'nominations' ? 'nomination' : 'service'}
                    onStatusUpdate={fetchData}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminDashboardPage
